import { z } from "zod";
import { BusinessDate } from "./common.js";
import { Id } from "./ids.js";
import { PaymentMethod } from "./finance.js";

// Query schema
export const DailyReportQuery = z.object({
  date: BusinessDate.optional(),
});
export type DailyReportQuery = z.infer<typeof DailyReportQuery>;

// Sales Section
export const DailyReportOrgSales = z.object({
  organizationId: Id,
  organizationName: z.string(),
  portions: z.number().int().nonnegative(),
  revenueRial: z.number().int().nonnegative(),
  missingPricePortions: z.number().int().nonnegative().default(0),
});
export type DailyReportOrgSales = z.infer<typeof DailyReportOrgSales>;

export const DailyReportSales = z.object({
  organizations: z.array(DailyReportOrgSales),
  totalPortions: z.number().int().nonnegative(),
  totalRevenueRial: z.number().int().nonnegative(),
  totalMissingPricePortions: z.number().int().nonnegative().default(0),
});
export type DailyReportSales = z.infer<typeof DailyReportSales>;

// Gross Profit Section (visible only when seeCost is true)
export const DailyReportProfit = z.object({
  revenueRial: z.number().int().nonnegative(),
  costRial: z.number().int().nonnegative(),
  grossProfitRial: z.number().int(),
  profitPerPortionRial: z.number().int(),
  marginPerMille: z.number().int(),
  upgradeCostRial: z.number().int().nonnegative().default(0),
  missingCostPortions: z.number().int().nonnegative().default(0),
  hasMissingData: z.boolean().default(false),
});
export type DailyReportProfit = z.infer<typeof DailyReportProfit>;

// Stock Used Section
export const DailyReportStockItem = z.object({
  itemId: Id,
  itemCode: z.string(),
  itemName: z.string(),
  baseUnit: z.string(),
  issueQuantityMilli: z.number().int().nonnegative(),
  wasteQuantityMilli: z.number().int().nonnegative(),
  totalQuantityMilli: z.number().int().nonnegative(),
  unitPriceRial: z.number().int().nonnegative().nullable(),
  totalValueRial: z.number().int().nonnegative().nullable(),
  isMissingPrice: z.boolean().default(false),
});
export type DailyReportStockItem = z.infer<typeof DailyReportStockItem>;

export const DailyReportStockUsed = z.object({
  items: z.array(DailyReportStockItem),
  issueVouchersCount: z.number().int().nonnegative().default(0),
  wasteVouchersCount: z.number().int().nonnegative().default(0),
  totalStockValueRial: z.number().int().nonnegative().nullable(),
  hasMissingPrice: z.boolean().default(false),
});
export type DailyReportStockUsed = z.infer<typeof DailyReportStockUsed>;

// Money Received Section
export const DailyReportPaymentByMethod = z.object({
  method: PaymentMethod,
  count: z.number().int().nonnegative(),
  totalAmountRial: z.number().int().nonnegative(),
});
export type DailyReportPaymentByMethod = z.infer<typeof DailyReportPaymentByMethod>;

export const DailyReportPaymentItem = z.object({
  paymentId: Id,
  organizationId: Id,
  organizationName: z.string(),
  method: PaymentMethod,
  amountRial: z.number().int().nonnegative(),
  referenceNumber: z.string().default(""),
  chequeNumber: z.string().nullable().optional(),
  chequeBank: z.string().nullable().optional(),
  chequeDueDate: BusinessDate.nullable().optional(),
  chequeStatus: z.string().nullable().optional(),
  notes: z.string().default(""),
});
export type DailyReportPaymentItem = z.infer<typeof DailyReportPaymentItem>;

export const DailyReportMoneyReceived = z.object({
  methods: z.array(DailyReportPaymentByMethod),
  payments: z.array(DailyReportPaymentItem),
  totalReceivedRial: z.number().int().nonnegative(),
  chequesCount: z.number().int().nonnegative().default(0),
  chequesTotalRial: z.number().int().nonnegative().default(0),
});
export type DailyReportMoneyReceived = z.infer<typeof DailyReportMoneyReceived>;

// Overall Daily Report
export const DailyReportResponse = z.object({
  date: BusinessDate,
  jalaliDate: z.string(),
  canSeeCost: z.boolean(),
  sales: DailyReportSales,
  profit: DailyReportProfit.nullable(),
  stockUsed: DailyReportStockUsed,
  moneyReceived: DailyReportMoneyReceived,
  generatedAt: z.string(),
});
export type DailyReportResponse = z.infer<typeof DailyReportResponse>;

/** Formats an ISO YYYY-MM-DD date to Persian Jalali YYYY/MM/DD. */
export function toJalaliString(isoDate: string): string {
  const [yStr, mStr, dStr] = isoDate.split("-");
  const y = Number(yStr ?? "2026");
  const m = Number(mStr ?? "1");
  const d = Number(dStr ?? "1");
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const parts = new Intl.DateTimeFormat("en-US-u-ca-persian", {
    timeZone: "Asia/Tehran",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(dt);

  const jYear = parts.find((p) => p.type === "year")?.value ?? "";
  const jMonth = parts.find((p) => p.type === "month")?.value ?? "";
  const jDay = parts.find((p) => p.type === "day")?.value ?? "";
  return `${jYear}/${jMonth}/${jDay}`;
}
